import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import UnifiedMenu from '../components/UnifiedMenu';
import EmotionGrid from '../components/EmotionGrid';
import ShareMoodCard from '../components/ShareMoodCard';
import AchievementsPanel from '../components/AchievementsPanel';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { ChevronLeft, ArrowRight, Menu, Settings, Sparkles, ExternalLink, Users } from 'lucide-react';
import { HOFFMAN_CATEGORIES, getCategoryNames } from '../hoffmanModel';
import { getRecommendations, getQuoteForEmotion } from '../recommendations';
import { useLanguage } from '../contexts/LanguageContext';
import { translateEmotionCategory, translateFeeling, getTranslatedQuote } from '../translations';

export default function DashboardV2({ user, onLogout }) {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  // Multi-step state
  const [step, setStep] = useState(1); // 1: Category, 2: Feeling, 3: Body, 4: Context, 5: Recommendations
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedFeeling, setSelectedFeeling] = useState(null);
  const [emotionalIntensity, setEmotionalIntensity] = useState(5);
  const [reason, setReason] = useState('');
  const [tags, setTags] = useState('');
  const [showRecommendations, setShowRecommendations] = useState(false);
  const [currentQuote, setCurrentQuote] = useState('');
  const [sentimentData, setSentimentData] = useState(null);
  const [showJournal, setShowJournal] = useState(false);
  const [currentTheme, setCurrentTheme] = useState(null);
  const [activeRegion, setActiveRegion] = useState(null);
  const [gender, setGender] = useState(user?.gender || 'male');
  
  // Stats
  const [recentMoods, setRecentMoods] = useState([]);
  const [stats, setStats] = useState(null);

  // Set initial quote based on language
  useEffect(() => {
    if (!currentQuote && language) {
      const defaultQuotes = {
        'en': 'Your emotional health matters. Take care of yourself.',
        'hi': 'आपका भावनात्मक स्वास्थ्य महत्वपूर्ण है। अपना ख्याल रखें।',
        'es': 'Tu salud emocional importa. Cuídate.',
        'fr': 'Votre santé émotionnelle compte. Prenez soin de vous.',
        'de': 'Ihre emotionale Gesundheit ist wichtig. Passen Sie auf sich auf.',
        'ar': 'صحتك العاطفية مهمة. اعتني بنفسك.'
      };
      setCurrentQuote(defaultQuotes[language] || defaultQuotes['en']);
    }
  }, [language]);

  useEffect(() => {
    fetchRecentMoods();
    fetchStats();
  }, []);

  // Apply COMPREHENSIVE dynamic theme when category is selected
  useEffect(() => {
    if (selectedCategory) {
      const category = HOFFMAN_CATEGORIES[selectedCategory];
      if (category) {
        const theme = {
          primary: category.color,
          background: `linear-gradient(135deg, ${category.color}25 0%, #FFFFFF 40%, ${category.color}15 100%)`,
          cardBg: `linear-gradient(135deg, ${category.color}12 0%, #FFFFFF 100%)`,
          textAccent: category.color,
          buttonBg: category.color,
          borderColor: category.color
        };
        setCurrentTheme(theme);
        
        // Apply comprehensive theming to document
        document.body.style.background = theme.background;
        document.body.style.transition = 'background 0.8s ease';
        
        // Apply CSS variables for consistent theming
        document.documentElement.style.setProperty('--emotion-primary', category.color);
        document.documentElement.style.setProperty('--emotion-light', `${category.color}15`);
        document.documentElement.style.setProperty('--emotion-medium', `${category.color}30`);
      }
    } else {
      resetTheme();
    }
  }, [selectedCategory]);

  const resetTheme = () => {
    setCurrentTheme(null);
    document.body.style.background = 'linear-gradient(to bottom, #ffffff, #fafaf9, #f5f5f4)';
    document.documentElement.style.removeProperty('--emotion-primary');
    document.documentElement.style.removeProperty('--emotion-light');
    document.documentElement.style.removeProperty('--emotion-medium');
  };

  const fetchRecentMoods = async () => {
    try {
      const res = await axios.get(`${API}/moods?limit=5`);
      setRecentMoods(res.data);
    } catch (error) {
      console.error('Failed to fetch moods', error);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/moods/stats`);
      setStats(res.data);
    } catch (error) {
      console.error('Failed to fetch stats', error);
    }
  };

  const handleCategorySelect = async (categoryName) => {
    setSelectedCategory(categoryName);
    setSelectedFeeling(null);
    setStep(2);
    
    // Fetch dynamic quote for this emotion
    try {
      const quoteRes = await axios.get(`${API}/quotes/emotion/${encodeURIComponent(categoryName)}`);
      if (quoteRes.data && quoteRes.data.quote) {
        const quoteText = `${quoteRes.data.quote} — ${quoteRes.data.author}`;
        setCurrentQuote(quoteText);
      }
    } catch (error) {
      console.error('Failed to fetch quote:', error);
      // Fallback to translated quote
      const translatedQuote = getTranslatedQuote(categoryName, language);
      setCurrentQuote(translatedQuote || getQuoteForEmotion(categoryName));
    }
  };

  const handleFeelingSelect = (feeling) => {
    setSelectedFeeling(feeling);
    setStep(4); // Skip body sensations (Step 3), go directly to context
  };

  const handleSubmit = async () => {
    if (!selectedCategory || !selectedFeeling) {
      toast.error('Please complete all steps');
      return;
    }

    try {
      await axios.post(`${API}/moods/v2`, {
        primary_category: selectedCategory,
        specific_feeling: selectedFeeling,
        body_sensations: [], // No body sensations tracked
        emotional_intensity: emotionalIntensity,
        physical_intensity: 0,
        reason: reason || null,
        tags: tags ? tags.split(',').map(t => t.trim()) : []
      });
      
      toast.success('Emotional state recorded');
      setShowRecommendations(true);
      setStep(5);
      fetchRecentMoods();
      fetchStats();
      // Fetch sentiment data after recording
      axios.get(`${API}/sentiment/regional`).then(r => setSentimentData(r.data)).catch(() => {});
    } catch (error) {
      toast.error('Failed to record emotion');
      console.error(error);
    }
  };

  const handleActionClick = (actionTitle, actionDescription) => {
    const fullText = `${actionTitle} ${actionDescription}`.toLowerCase();
    
    // Returns true if action opens external link/app
    const hasExternalAction = 
      fullText.includes('music') || fullText.includes('soothing') || fullText.includes('listen') ||
      fullText.includes('reach out') || fullText.includes('talk') || fullText.includes('call') || fullText.includes('loved one') || fullText.includes('friend') || fullText.includes('connect') ||
      fullText.includes('nature') || fullText.includes('outdoors') || fullText.includes('outside') || fullText.includes('park') || fullText.includes('walk') ||
      fullText.includes('creative') || fullText.includes('art') || fullText.includes('draw') || fullText.includes('paint') ||
      fullText.includes('yoga') || fullText.includes('tai chi') || fullText.includes('stretch') ||
      fullText.includes('breath') || fullText.includes('meditation') ||
      fullText.includes('sleep') || fullText.includes('rest') || fullText.includes('nap') ||
      fullText.includes('exercise') || fullText.includes('run') || fullText.includes('workout') ||
      fullText.includes('explore') || fullText.includes('learn') ||
      fullText.includes('self-compassion') || fullText.includes('self-care') || fullText.includes('bath');
    
    // Only proceed if has external action
    if (!hasExternalAction) {
      return false; // Don't show as clickable
    }
    
    // Execute the action
    if (fullText.includes('music')) { navigate('/music'); return true; }
    if (fullText.includes('journal') || fullText.includes('write')) { setShowJournal(true); return true; }
    if (fullText.includes('therapist') || fullText.includes('professional')) { navigate('/chat'); return true; }
    if (fullText.includes('reach out') || fullText.includes('talk') || fullText.includes('call')) {
      window.open('https://web.whatsapp.com/', '_blank');
      toast.success('Opening WhatsApp 💚');
      return true;
    }
    if (fullText.includes('nature') || fullText.includes('outdoors') || fullText.includes('walk')) {
      window.open('https://www.google.com/maps/search/parks+near+me', '_blank');
      toast.success('Finding nature spots 🌳');
      return true;
    }
    if (fullText.includes('creative') || fullText.includes('art')) {
      window.open('https://www.youtube.com/results?search_query=art+therapy+relaxation', '_blank');
      toast.success('Opening art tutorials 🎨');
      return true;
    }
    if (fullText.includes('yoga') || fullText.includes('stretch')) {
      window.open('https://www.youtube.com/results?search_query=10+minute+yoga+relaxation', '_blank');
      toast.success('Opening yoga videos 🧘');
      return true;
    }
    if (fullText.includes('breath') || fullText.includes('meditation')) {
      window.open('https://www.youtube.com/results?search_query=5+minute+breathing+exercise', '_blank');
      toast.success('Opening breathing exercises 🌬️');
      return true;
    }
    if (fullText.includes('sleep') || fullText.includes('rest')) {
      window.open('https://www.youtube.com/results?search_query=sleep+meditation+music', '_blank');
      toast.success('Opening sleep content 😴');
      return true;
    }
    if (fullText.includes('exercise') || fullText.includes('workout')) {
      window.open('https://www.youtube.com/results?search_query=quick+home+workout', '_blank');
      toast.success('Opening workouts 💪');
      return true;
    }
    if (fullText.includes('explore') || fullText.includes('learn')) {
      window.open('https://www.youtube.com/results?search_query=learn+something+new', '_blank');
      toast.success('Opening learning resources 📚');
      return true;
    }
    if (fullText.includes('self-care') || fullText.includes('bath')) {
      window.open('https://www.youtube.com/results?search_query=self+care+relaxation', '_blank');
      toast.success('Opening self-care guides 🌸');
      return true;
    }
    
    return false;
  };

  // Helper to check if action has external link
  const hasExternalLink = (actionTitle, actionDescription) => {
    const fullText = `${actionTitle} ${actionDescription}`.toLowerCase();
    return fullText.includes('music') || fullText.includes('reach out') || fullText.includes('nature') || 
           fullText.includes('creative') || fullText.includes('yoga') || fullText.includes('breath') || 
           fullText.includes('sleep') || fullText.includes('exercise') || fullText.includes('explore') || 
           fullText.includes('self-care');
  };

  const resetForm = () => {
    setStep(1);
    setSelectedCategory(null);
    setSelectedFeeling(null);
    setEmotionalIntensity(5);
    setReason('');
    setTags('');
    setShowRecommendations(false);
    setShowJournal(false);
    resetTheme();
  };

  const categoryData = selectedCategory ? HOFFMAN_CATEGORIES[selectedCategory] : null;

  return (
    <div className="h-screen flex flex-col bg-gradient-to-b from-white via-stone-50 to-stone-100 relative overflow-hidden">
      {/* Transparent Logo Background */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-5">
        <img 
          src="/logo512.png" 
          alt="Background"
          className="w-96 h-96 object-contain"
        />
      </div>

      {/* Main Content */}
      <div className="relative z-10 h-full flex flex-col">
      {/* Single Unified Menu - Navigation + Stats */}
      <UnifiedMenu 
        active="dashboard"
        stats={stats}
        currentQuote={currentQuote}
        recentMoods={recentMoods}
        onLogout={onLogout}
      />
      
      {/* Header - Only Settings icon now */}
      <div className="flex-none px-4 py-3 flex items-center justify-between border-b border-stone-200 bg-white/80">
        <h1 className="text-xl md:text-2xl font-semibold text-warm-charcoal truncate" style={{ fontFamily: 'Playfair Display, serif' }}>
          Welcome, {user.name}
        </h1>
        <button
          onClick={() => navigate('/settings')}
          className="p-2 hover:bg-stone-100 rounded-full transition-colors"
        >
          <Settings className="w-5 h-5 text-warm-charcoal/60" />
        </button>
      </div>

      {/* Main Content - Fills remaining space, no bottom nav needed */}
      <div className="flex-1 overflow-hidden px-3 py-2">
        <div className="h-full flex flex-col zen-card p-3 md:p-4">

            {/* Scrollable content area */}
            <div className="flex-1 overflow-y-auto">

            {/* Step 1: Emotion Grid Selection */}
            {step === 1 && (
              <div className="fade-in h-full flex flex-col">
                <div className="text-center flex-none py-1">
                  <h2 className="text-base md:text-lg font-semibold" style={{ fontFamily: 'Playfair Display, serif' }}>
                    How are you feeling, {user.name}?
                  </h2>
                  <p className="text-xs text-warm-charcoal/60">
                    {t('browse_emotions')}
                  </p>
                </div>

                <div className="flex-1 flex items-center justify-center min-h-0" style={{ touchAction: 'manipulation', overflow: 'hidden' }}>
                  <EmotionGrid onCategorySelect={handleCategorySelect} />
                </div>
              </div>
            )}

            {/* Step 2: Specific Feeling Selection with Dynamic Theming */}
            {step === 2 && categoryData && (
              <div className="space-y-6 fade-in">
                <div className="flex items-center gap-4 mb-6">
                  <button
                    onClick={() => setStep(1)}
                    className="p-2 rounded-full hover:bg-stone-100 transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <div
                        className="w-10 h-10 rounded-full shadow-lg animate-pulse-slow"
                        style={{ backgroundColor: categoryData.color }}
                      />
                      <h2 
                        className="text-3xl font-bold" 
                        style={{ 
                          fontFamily: 'Playfair Display, serif',
                          color: currentTheme?.primary || '#1C1917'
                        }}
                      >
                        {translateEmotionCategory(selectedCategory, language)}
                      </h2>
                    </div>
                    <p className="text-sm text-warm-charcoal/60">
                      {t('which_specific_feeling')}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {categoryData.feelings.map((feeling) => (
                    <button
                      key={feeling}
                      onClick={() => handleFeelingSelect(feeling)}
                      className="px-4 py-3 rounded-xl border-2 transition-all duration-300 font-medium text-sm hover:shadow-lg hover:scale-105"
                      style={{
                        borderColor: currentTheme?.borderColor || '#e5e7eb',
                        background: `linear-gradient(135deg, ${currentTheme?.primary}08 0%, white 100%)`
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = currentTheme?.primary || '#3A5A40';
                        e.currentTarget.style.background = `${currentTheme?.primary}15`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = currentTheme?.borderColor || '#e5e7eb';
                        e.currentTarget.style.background = `linear-gradient(135deg, ${currentTheme?.primary}08 0%, white 100%)`;
                      }}
                    >
                      {translateFeeling(feeling, language)}
                    </button>
                  ))}
                </div>
              </div>
            )}


            {/* Step 4: Context & Intensity - Now Step 3 */}
            {step === 4 && (
              <div className="space-y-6 fade-in">
                <div className="flex items-center gap-4 mb-6">
                  <button
                    onClick={() => setStep(2)}
                    className="p-2 rounded-full hover:bg-stone-100 transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <div>
                    <h2 className="text-2xl font-semibold" style={{ fontFamily: 'Playfair Display, serif' }}>
                      {t('add_context_intensity')}
                    </h2>
                  </div>
                </div>

                {/* Summary Card - No body sensations */}
                <div 
                  className="p-6 rounded-2xl border-2 shadow-lg"
                  style={{
                    borderColor: categoryData?.color,
                    background: '#F9FAFB'
                  }}
                >
                  <div className="flex items-start gap-4">
                    <div
                      className="w-12 h-12 rounded-full flex-shrink-0 shadow-md"
                      style={{ backgroundColor: categoryData?.color }}
                    />
                    <div>
                      <p className="text-sm font-medium text-warm-charcoal/60 uppercase tracking-wider">{t('your_emotional_state')}</p>
                      <p 
                        className="text-xl font-semibold mt-1" 
                        style={{ color: '#1C1917' }}
                      >
                        {translateFeeling(selectedFeeling, language)}
                      </p>
                      <p className="text-sm text-warm-charcoal/70 mt-1">{t('category')}: {translateEmotionCategory(selectedCategory, language)}</p>
                    </div>
                  </div>
                </div>

                {/* Emotional Intensity */}
                <div className="bg-white/50 p-6 rounded-2xl border border-stone-200">
                  <label className="text-sm font-medium text-warm-charcoal/80 uppercase tracking-wider mb-4 block">
                    {t('how_intense')}
                  </label>
                  <div className="relative">
                    <input
                      type="range"
                      min="1"
                      max="10"
                      step="1"
                      value={emotionalIntensity}
                      onChange={(e) => setEmotionalIntensity(parseInt(e.target.value))}
                      className="spectrum-slider w-full"
                      style={{
                        background: `linear-gradient(to right, #E7E5E4 0%, #3A5A40 ${(emotionalIntensity - 1) * 11.11}%, #E7E5E4 ${(emotionalIntensity - 1) * 11.11}%, #E7E5E4 100%)`
                      }}
                    />
                    <div className="flex justify-between text-xs text-warm-charcoal/40 mt-2 px-1">
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                        <span key={num} className={emotionalIntensity === num ? 'font-bold text-hunter-green' : ''}>
                          {num}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-xs text-warm-charcoal/60 uppercase tracking-wider">{t('mild')}</span>
                    <div className="px-6 py-3 rounded-full bg-hunter-green text-white font-bold text-2xl" style={{ fontFamily: 'Playfair Display, serif' }}>
                      {emotionalIntensity}
                    </div>
                    <span className="text-xs text-warm-charcoal/60 uppercase tracking-wider">{t('strong')}</span>
                  </div>
                </div>

                {/* Reason */}
                <div className="space-y-3">
                  <label className="text-sm font-medium text-warm-charcoal/80 uppercase tracking-wider">
                    {t('whats_reason')}
                  </label>
                  <Textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder={t('why_feeling_this')}
                    className="rounded-xl border-stone-200 bg-white/50 focus:border-hunter-green/50 focus:ring-4 focus:ring-hunter-green/10 p-4 transition-all duration-500"
                    rows={3}
                  />
                </div>

                {/* Tags */}
                <div className="space-y-3">
                  <label className="text-sm font-medium text-warm-charcoal/80 uppercase tracking-wider">{t('add_tags')}</label>
                  <Input
                    value={tags}
                    onChange={(e) => setTags(e.target.value)}
                    placeholder={t('example_tags')}
                    className="rounded-xl border-stone-200 bg-white/50 focus:border-hunter-green/50 focus:ring-4 focus:ring-hunter-green/10 h-12 px-4 transition-all duration-500"
                  />
                </div>

                <Button
                  onClick={handleSubmit}
                  className="w-full zen-button text-white hover:opacity-90 text-lg py-6"
                  style={{
                    background: currentTheme?.buttonBg || '#3A5A40',
                    boxShadow: `0 10px 25px ${currentTheme?.primary}35`
                  }}
                >
                  {t('save_track_emotion')}
                </Button>
              </div>
            )}

            {/* Step 5: Recommendations */}
            {step === 5 && showRecommendations && (
              <div className="space-y-6 fade-in">
                <div className="text-center mb-8">
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-100 text-green-700 mb-4">
                    <Sparkles className="w-5 h-5" />
                    <span className="font-semibold">Emotional State Recorded!</span>
                  </div>
                  <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Recommendations for You
                  </h2>
                  <p className="text-warm-charcoal/60">{t('based_on_feeling')}: <span className="font-semibold text-hunter-green">{translateFeeling(selectedFeeling, language)}</span></p>
                  <p className="text-sm text-hunter-green/70 mt-2 font-medium">
                    {t('click_action_below')}
                  </p>
                </div>

                {(() => {
                  const recommendations = getRecommendations(selectedCategory);
                  return (
                    <>
                      {/* Sentiment Banner — personal */}
                      {sentimentData && sentimentData.distribution && (
                        <div className="p-5 rounded-2xl bg-gradient-to-br from-hunter-green/10 via-sage/10 to-hunter-green/5 border-2 border-hunter-green/25 shadow-sm" data-testid="sentiment-after-checkin">
                          <div className="text-center mb-2">
                            <Users className="w-6 h-6 text-hunter-green mx-auto mb-1.5" />
                            <p className="text-xs text-warm-charcoal/50 uppercase tracking-wider font-semibold">You're not alone</p>
                          </div>
                          <p className="text-center text-sm text-warm-charcoal leading-relaxed" style={{ fontFamily: 'Playfair Display, serif' }}>
                            Trust us, around{' '}
                            <span className="text-xl font-bold text-hunter-green">
                              {sentimentData.distribution[sentimentData.user_sentiment] 
                                ? `${sentimentData.distribution[sentimentData.user_sentiment]}%` 
                                : `${Math.round(sentimentData.similar_count / sentimentData.total_tracking * 100)}%`}
                            </span>
                            {' '}of people{sentimentData.region !== 'global' ? ` in ${sentimentData.region}` : ' around the globe'} are feeling{' '}
                            <span className="font-bold text-hunter-green italic">"{selectedFeeling}"</span>
                            {' '}right this moment!
                          </p>
                          <p className="text-[10px] text-warm-charcoal/40 text-center mt-2">
                            Based on {sentimentData.total_tracking.toLocaleString()} emotional check-ins
                          </p>
                        </div>
                      )}

                      {/* Quote - Dynamic from API */}
                      <div className="p-6 rounded-2xl bg-gradient-to-br from-sage/20 to-white border-2 border-sage/30">
                        <p className="text-lg text-warm-charcoal italic leading-relaxed text-center" style={{ fontFamily: 'Playfair Display, serif' }}>
                          "{currentQuote || getTranslatedQuote(selectedCategory, language) || recommendations.quote}"
                        </p>
                      </div>

                      {/* Action Items - Journal & Music always navigate, others check for links */}
                      <div className="space-y-2">
                        <h3 className="font-semibold text-sm">{t('try_these_actions')}</h3>
                        {recommendations.actions.map((action, index) => {
                          // First 2 actions are always Journal & Music — make them navigate
                          const isJournal = action.title.includes('Journal');
                          const isMusic = action.title.includes('Music');
                          const isNavigable = isJournal || isMusic;
                          const hasLink = isNavigable || hasExternalLink(action.title, action.description);
                          
                          const handleClick = () => {
                            if (isJournal) navigate('/journal');
                            else if (isMusic) navigate('/music');
                            else handleActionClick(action.title, action.description);
                          };
                          
                          return hasLink ? (
                            <button
                              key={index}
                              onClick={handleClick}
                              data-testid={`action-${isJournal ? 'journal' : isMusic ? 'music' : index}`}
                              className="w-full p-3 rounded-xl bg-white border-2 border-stone-200 hover:border-hunter-green hover:bg-hunter-green/5 transition-colors text-left group"
                              style={{ touchAction: 'manipulation' }}
                            >
                              <div className="flex items-start gap-3">
                                <div className="text-2xl flex-shrink-0">{action.icon}</div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <h4 className="font-semibold text-sm group-hover:text-hunter-green truncate">
                                      {action.title}
                                    </h4>
                                    <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-hunter-green/10 text-hunter-green font-medium shrink-0">
                                      {isNavigable ? 'Go' : 'Tap'}
                                    </span>
                                  </div>
                                  <p className="text-xs text-warm-charcoal/70 line-clamp-1">{action.description}</p>
                                </div>
                              </div>
                            </button>
                          ) : (
                            <div
                              key={index}
                              className="w-full p-3 rounded-xl bg-stone-50 border border-stone-200"
                            >
                              <div className="flex items-start gap-3">
                                <div className="text-2xl flex-shrink-0">{action.icon}</div>
                                <div className="flex-1">
                                  <h4 className="font-semibold text-sm text-warm-charcoal">
                                    {action.title}
                                  </h4>
                                  <p className="text-xs text-warm-charcoal/70 mt-0.5">{action.description}</p>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Share Your Mood Card - Compact */}
                      <ShareMoodCard
                        feeling={selectedFeeling}
                        category={selectedCategory}
                        intensity={emotionalIntensity}
                        quote={currentQuote || recommendations.quote}
                        userName={user?.name}
                      />

                      <Button
                        onClick={resetForm}
                        className="w-full zen-button bg-hunter-green text-white text-sm py-3 mt-2"
                      >
                        {t('track_another')}
                      </Button>
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Journal Modal */}
      {showJournal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-6" onClick={() => setShowJournal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>
                Journaling Space
              </h2>
              <button
                onClick={() => setShowJournal(false)}
                className="p-2 hover:bg-stone-100 rounded-full transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <Textarea
              placeholder="Write your thoughts, reflections, or gratitude list here..."
              className="w-full h-64 p-4 rounded-xl border-stone-200 focus:border-hunter-green/50 focus:ring-4 focus:ring-hunter-green/10"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            <div className="flex gap-3 mt-4">
              <Button
                onClick={() => {
                  toast.success('Journal entry saved to your reflection');
                  setShowJournal(false);
                }}
                className="flex-1 zen-button bg-hunter-green text-white"
              >
                Save to Reflection
              </Button>
              <Button
                onClick={() => setShowJournal(false)}
                className="px-6 zen-button bg-white text-warm-charcoal border border-stone-200"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
